import { useCallback } from 'react';

import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Textarea } from '@components/ui/textarea';
import { pages } from '@constants/pages';
import { topic } from '@constants/topic';
import errorsValidation from '@errors/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ICreateSuggestInputData } from '@services/suggests/dtos/createSuggest/InputData';
import SuggestService from '@services/suggests/SuggestService';
import { useMutation } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  description: z.string().min(1, {
    message: errorsValidation.required,
  }),
  area: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function SuggestedTopic() {
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      description: '',
      area: '',
    },
  });

  const { isPending, mutateAsync } = useMutation<
    void,
    unknown,
    ICreateSuggestInputData
  >({
    mutationFn: SuggestService.create,
    onError: () => {
      toast.error('Atenção', {
        description: 'Não foi possível enviar a sugestão. Tente novamente',
      });
    },
    onSuccess: () => {
      toast.success('Sucesso!', {
        description: 'Sugestão enviada com sucesso',
      });
      navigate(pages.home, {
        replace: true,
      });
    },
  });

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      await mutateAsync({
        area: values.area,
        descricao: values.description,
      });
    },
    [mutateAsync],
  );

  return (
    <div>
      <PageTitle hasBackButton title="Sugerir um tema" />
      <button
        className="hidden"
        onClick={() => {
          const a: any = null;
          console.log(a.test.test);
          throw new Error('This is your first error!');
        }}>
        Break the world
      </button>
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="area"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row justify-between">
                  <FormLabel className="text-tertiary">Area</FormLabel>
                  <FormMessage />
                </div>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger onBlur={field.onBlur}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {topic.inputPickerOptions.map((gender) => (
                      <SelectItem
                        className="focus:bg-tertiary"
                        key={gender.value}
                        value={gender.value}>
                        {gender.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row justify-between">
                  <FormLabel className="text-tertiary">Sugestão</FormLabel>
                  <FormMessage />
                </div>
                <FormControl>
                  <Textarea className="min-h-48 resize-none" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-[repeat(2,_15rem)] sm:justify-end">
            <Button
              asChild
              disabled={isPending}
              type="button"
              variant="destructive">
              <Link replace to={pages.home}>
                Cancelar
              </Link>
            </Button>
            <Button disabled={isPending} type="submit" variant="secondary">
              {isPending ? (
                <LoaderCircle className="text-background animate-spin" />
              ) : (
                'Salvar'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default SuggestedTopic;
